import React, { useState, useEffect } from 'react'
import { FileText, Download, Share2, Eye, File, GripVertical } from 'lucide-react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useBlobUrlFromApi } from '../hooks/useBlobUrl'

/**
 * OtherFileList component for displaying other files in a list format
 * Handles PDFs, documents, and other file types with reordering capabilities
 */
const OtherFileList = ({ 
  file, 
  pageType = 'satsueiDataToroku',
  imageService,
  serviceParams = [],
  className = '',
  onDownload = null,
  onShare = null,
  onPreview = null,
  isSelected = false,
  isSelectionMode = false,
  onToggleItemSelection = null
}) => {
  // Sortable functionality
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `other-${file.objectOtherFileId}` })

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const [fileType, setFileType] = useState('unknown')
  const [fileSize, setFileSize] = useState(0)

  // Create stable dependencies to prevent unnecessary re-fetches
  const fileKey = `${file.objectId}-${file.objectOtherFileId}-${file.filenameExtension}-${file.isUploaded || false}`
  
  // Early check to prevent hook from running if we know the file shouldn't be loaded
  const shouldSkipFileLoad = file.isUploaded === 0 || file.isUploaded === "0" || file.isUploaded === false

  // Get MIME type based on file extension
  const getMimeType = (extension) => {
    if (!extension) return 'application/octet-stream'
    const ext = extension.toLowerCase()
    const mimeTypes = {
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'txt': 'text/plain',
      'rtf': 'application/rtf',
      'zip': 'application/zip',
      'rar': 'application/x-rar-compressed',
      '7z': 'application/x-7z-compressed'
    }
    return mimeTypes[ext] || 'application/octet-stream'
  }

  // Use blob URL hook for file content
  const { blobUrl: fileUrl, isLoading, error } = useBlobUrlFromApi(
    shouldSkipFileLoad ? null : (async () => {
      
      // Add timeout to prevent long loading
      const timeout = pageType === 'satsueiDataKensaku' ? 3000 : 5000
      const fetchPromise = imageService.getOtherFileContent(
        file.objectId,
        file.objectOtherFileId,
        file.filenameExtension
      )
      
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('File fetch timeout')), timeout)
      })
      
      const result = await Promise.race([fetchPromise, timeoutPromise])
      
      if (result && result.data) {
        // Return the blob data with correct MIME type
        const mimeType = getMimeType(file.filenameExtension)
        if (result.data.type !== mimeType) {
          const newBlob = new Blob([result.data], { type: mimeType })
          return newBlob
        }
        return result.data
      }
      return null
    }),
    [fileKey, imageService, serviceParams, shouldSkipFileLoad],
    getMimeType(file.filenameExtension)
  )

  // Determine file type and size
  useEffect(() => {
    if (file.filenameExtension) {
      setFileType(file.filenameExtension.toLowerCase())
    }
    if (file.fileSize) {
      setFileSize(file.fileSize)
    }
  }, [file.filenameExtension, file.fileSize])

  // Get file icon based on type
  const getFileIcon = () => {
    const iconClass = "h-8 w-8 text-gray-500"
    
    switch (fileType) {
      case 'pdf':
        return <FileText className={`${iconClass} text-red-500`} />
      case 'doc':
      case 'docx':
        return <FileText className={`${iconClass} text-blue-500`} />
      case 'xls':
      case 'xlsx':
        return <FileText className={`${iconClass} text-green-500`} />
      case 'ppt':
      case 'pptx':
        return <FileText className={`${iconClass} text-orange-500`} />
      case 'txt':
        return <FileText className={`${iconClass} text-gray-500`} />
      case 'zip':
      case 'rar':
      case '7z':
        return <FileText className={`${iconClass} text-purple-500`} />
      default:
        return <File className={iconClass} />
    }
  }

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handleDownload = async (e) => {
    e.stopPropagation()
    
    if (onDownload) {
      onDownload(file)
    } else {
      try {
        // Use original filename if available, otherwise construct from object data
        const filename = file.originalFilename || 
                        file.filename || 
                        `${file.objectId}_${file.objectOtherFileId}.${file.filenameExtension}`
        
        
        // Fetch the file data directly from the API
        let response
        try {
          
          // Test the service method call
          try {
            const testCall = imageService.getOtherFileContent(
              file.objectId,
              file.objectOtherFileId,
              file.filenameExtension
            )
          } catch (testError) {
            console.error('OtherFileList: Service method test failed:', testError)
          }
          
          // Add timeout to prevent hanging
          const apiPromise = imageService.getOtherFileContent(
            file.objectId,
            file.objectOtherFileId,
            file.filenameExtension
          )
          
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => {
              reject(new Error('API call timeout after 10 seconds'))
            }, 10000)
          })
          
          response = await Promise.race([apiPromise, timeoutPromise])
        } catch (apiError) {
          console.error('OtherFileList: API call failed', apiError)
          throw apiError
        }
        
        if (response && response.data) {
          // Create a new blob URL specifically for download
          const mimeType = getMimeType(file.filenameExtension)
          const downloadBlob = new Blob([response.data], { type: mimeType })
          const downloadUrl = URL.createObjectURL(downloadBlob)
          
          // Create download link
          const link = document.createElement('a')
          link.href = downloadUrl
          link.download = filename
          link.style.display = 'none'
          
          // Add to DOM, click, and remove
          document.body.appendChild(link)
          
          // Try to trigger download
          // Trigger download
          try {
            link.click()
          } catch (clickError) {
            console.error('OtherFileList: Link click failed', clickError)
          }
          
          // Clean up
          setTimeout(() => {
            if (document.body.contains(link)) {
              document.body.removeChild(link)
            }
            URL.revokeObjectURL(downloadUrl)
          }, 1000)
          
        } else {
          console.error('OtherFileList: No data received from API')
        }
        
      } catch (error) {
        console.error('OtherFileList: Download failed', {
          error: error,
          errorMessage: error?.message,
          errorStack: error?.stack,
          errorName: error?.name,
          fileData: {
            objectId: file.objectId,
            objectOtherFileId: file.objectOtherFileId,
            filenameExtension: file.filenameExtension
          }
        })
      }
    }
  }

  const handlePreview = (e) => {
    e.stopPropagation()
    
    if (onPreview) {
      onPreview(file, fileUrl)
    } else if (fileUrl) {
      try {
        const fileType = file.filenameExtension?.toLowerCase()
        if (['pdf'].includes(fileType) || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileType)) {
          const newWindow = window.open(fileUrl, '_blank')
          if (!newWindow) {
            console.warn('OtherFileList: Popup blocked, trying alternative method')
            // Fallback: create a temporary link and click it
            const link = document.createElement('a')
            link.href = fileUrl
            link.target = '_blank'
            link.rel = 'noopener noreferrer'
            link.style.display = 'none'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }
        } else {
          // For other file types, try to download instead of preview
          handleDownload(e)
          return
        }
        
      } catch (error) {
        console.error('OtherFileList: Preview failed', error)
      }
    } else {
      console.warn('OtherFileList: No file URL available for preview')
    }
  }

  const handleShare = (e) => {
    e.stopPropagation()
    
    if (onShare) {
      onShare(file, fileUrl)
    } else {
      // Default share behavior
      if (navigator.share && fileUrl) {
        navigator.share({
          title: file.originalFilename || file.filename || 'File',
          url: fileUrl
        }).catch(console.error)
      } else {
        // Fallback: copy URL to clipboard
        if (fileUrl) {
          navigator.clipboard.writeText(fileUrl)
          .catch(console.error)
        }
      }
    }
  }

  const handleCheckboxChange = (e) => {
    e.stopPropagation()
    if (onToggleItemSelection) {
      onToggleItemSelection(file.objectOtherFileId)
    }
  }

  // Show loading state
  if (isLoading) {
    return (
      <div 
        data-testid="file-loading-state"
        className={`flex items-center p-3 border-b border-gray-200 bg-gray-50 cursor-grab active:cursor-grabbing select-none ${className}`} 
        style={{
          ...dragStyle,
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none'
        }} 
        ref={setNodeRef} 
        {...listeners} 
        {...attributes}
        onDragStart={(e) => e.preventDefault()}
        onMouseDown={(e) => e.preventDefault()}
      >
        <div className="flex items-center space-x-3 flex-1">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <div>
            <p className="text-sm text-gray-500">読み込み中...</p>
          </div>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div 
        data-testid="file-error-state"
        className={`flex items-center p-3 border-b border-gray-200 bg-red-50 cursor-grab active:cursor-grabbing select-none ${className}`} 
        style={{
          ...dragStyle,
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none'
        }} 
        ref={setNodeRef} 
        {...listeners} 
        {...attributes}
        onDragStart={(e) => e.preventDefault()}
        onMouseDown={(e) => e.preventDefault()}
      >
        <div className="flex items-center space-x-3 flex-1">
          <File className="h-6 w-6 text-red-500" />
          <div>
            <p className="text-sm text-red-600">エラーが発生しました</p>
            <p className="text-xs text-red-500">ファイルを読み込めませんでした</p>
          </div>
        </div>
      </div>
    )
  }

  // Show no file state
  if (!fileUrl && !shouldSkipFileLoad) {
    return (
      <div 
        data-testid="file-not-found-state"
        className={`flex items-center p-3 border-b border-gray-200 bg-gray-50 cursor-grab active:cursor-grabbing select-none ${className}`} 
        style={{
          ...dragStyle,
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none'
        }} 
        ref={setNodeRef} 
        {...listeners} 
        {...attributes}
        onDragStart={(e) => e.preventDefault()}
        onMouseDown={(e) => e.preventDefault()}
      >
        <div className="flex items-center space-x-3 flex-1">
          <File className="h-6 w-6 text-gray-400" />
          <div>
            <p className="text-sm text-gray-500">ファイルが見つかりません</p>
          </div>
        </div>
      </div>
    )
  }

  // Show file list item
  return (
    <div 
      data-testid="other-file-item"
      className={`flex items-center p-3 border-b border-gray-200 hover:bg-gray-50 transition-colors select-none cursor-grab active:cursor-grabbing ${isSelected ? 'bg-blue-50 border-blue-200' : ''} ${isDragging ? 'opacity-50' : ''} ${className}`}
      style={{
        ...dragStyle,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none',
        WebkitTapHighlightColor: 'transparent'
      }}
      ref={setNodeRef}
      data-sortable-id={`other-${file.objectOtherFileId}`}
      {...listeners}
      {...attributes}
      onDragStart={(e) => {
        e.preventDefault()
      }}
      onMouseDown={(e) => {
        // Prevent text selection on mouse down
        if (e.detail > 1) {
          e.preventDefault()
        }
      }}
    >
      {/* Selection checkbox */}
      {isSelectionMode && (
        <div className="mr-3" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            data-testid="selection-checkbox"
            id={`selection-checkbox-${file.objectOtherFileId}`}
            checked={isSelected}
            onChange={handleCheckboxChange}
            className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500"
          />
        </div>
      )}

      {/* Drag handle - visual indicator only */}
      <div 
        data-testid="drag-handle"
        className="mr-3 p-1 text-gray-400" 
        style={{ touchAction: 'none' }}
        title="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </div>

      {/* File icon */}
      <div data-testid="file-icon" className="mr-3">
        {getFileIcon()}
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        <p data-testid="file-name" className="text-sm font-medium text-gray-900 truncate">
          {file.originalFilename || file.filename || `${file.objectId}_${file.objectOtherFileId}.${file.filenameExtension}`}
        </p>
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          <span data-testid="file-extension" className="uppercase">{file.filenameExtension}</span>
          {fileSize > 0 && (
            <>
              <span>•</span>
              <span data-testid="file-size">{formatFileSize(fileSize)}</span>
            </>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
        <button
          data-testid="preview-button"
          id={`preview-btn-${file.objectOtherFileId}`}
          onClick={handlePreview}
          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
          title="プレビュー"
        >
          <Eye className="h-4 w-4" />
        </button>
        <button
          data-testid="download-button"
          id={`download-btn-${file.objectOtherFileId}`}
          onClick={handleDownload}
          className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-full transition-colors"
          title="ダウンロード"
        >
          <Download className="h-4 w-4" />
        </button>
        {onShare && (
          <button
            data-testid="share-button"
            id={`share-btn-${file.objectOtherFileId}`}
            onClick={handleShare}
            className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-full transition-colors"
            title="共有"
          >
            <Share2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}

export default OtherFileList
